import React, { useEffect, useCallback, useRef } from 'react';

interface KeyboardNavigationOptions {
  onEnter?: () => void;
  onEscape?: () => void;
  onArrowUp?: () => void;
  onArrowDown?: () => void;
  onArrowLeft?: () => void;
  onArrowRight?: () => void;
  onTab?: (event?: KeyboardEvent) => void;
  onSpace?: () => void;
  onDelete?: () => void;
  onBackspace?: () => void;
  onHome?: () => void;
  onEnd?: () => void;
  onPageUp?: () => void;
  onPageDown?: () => void;
  onF1?: () => void;
  onF2?: () => void;
  onF3?: () => void;
  onF4?: () => void;
  onF5?: () => void;
  onF6?: () => void;
  onF7?: () => void;
  onF8?: () => void;
  onF9?: () => void;
  onF10?: () => void;
  onF11?: () => void;
  onF12?: () => void;
  preventDefault?: boolean;
  stopPropagation?: boolean;
}

export const useKeyboardNavigation = (options: KeyboardNavigationOptions = {}) => {
  const {
    onEnter,
    onEscape,
    onArrowUp,
    onArrowDown,
    onArrowLeft,
    onArrowRight,
    onTab,
    onSpace,
    onDelete,
    onBackspace,
    onHome,
    onEnd,
    onPageUp,
    onPageDown,
    onF1,
    onF2,
    onF3,
    onF4,
    onF5,
    onF6,
    onF7,
    onF8,
    onF9,
    onF10,
    onF11,
    onF12,
    preventDefault = true,
    stopPropagation = false
  } = options;

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (stopPropagation) {
      event.stopPropagation();
    }

    if (preventDefault) {
      event.preventDefault();
    }

    switch (event.key) {
      case 'Enter':
        onEnter?.();
        break;
      case 'Escape':
        onEscape?.();
        break;
      case 'ArrowUp':
        onArrowUp?.();
        break;
      case 'ArrowDown':
        onArrowDown?.();
        break;
      case 'ArrowLeft':
        onArrowLeft?.();
        break;
      case 'ArrowRight':
        onArrowRight?.();
        break;
      case 'Tab':
        onTab?.(event);
        break;
      case ' ':
        onSpace?.();
        break;
      case 'Delete':
        onDelete?.();
        break;
      case 'Backspace':
        onBackspace?.();
        break;
      case 'Home':
        onHome?.();
        break;
      case 'End':
        onEnd?.();
        break;
      case 'PageUp':
        onPageUp?.();
        break;
      case 'PageDown':
        onPageDown?.();
        break;
      case 'F1':
        onF1?.();
        break;
      case 'F2':
        onF2?.();
        break;
      case 'F3':
        onF3?.();
        break;
      case 'F4':
        onF4?.();
        break;
      case 'F5':
        onF5?.();
        break;
      case 'F6':
        onF6?.();
        break;
      case 'F7':
        onF7?.();
        break;
      case 'F8':
        onF8?.();
        break;
      case 'F9':
        onF9?.();
        break;
      case 'F10':
        onF10?.();
        break;
      case 'F11':
        onF11?.();
        break;
      case 'F12':
        onF12?.();
        break;
    }
  }, [
    onEnter,
    onEscape,
    onArrowUp,
    onArrowDown,
    onArrowLeft,
    onArrowRight,
    onTab,
    onSpace,
    onDelete,
    onBackspace,
    onHome,
    onEnd,
    onPageUp,
    onPageDown,
    onF1,
    onF2,
    onF3,
    onF4,
    onF5,
    onF6,
    onF7,
    onF8,
    onF9,
    onF10,
    onF11,
    onF12,
    preventDefault,
    stopPropagation
  ]);

  return { handleKeyDown };
};

// Hook for managing focus within a component
export const useFocusManagement = () => {
  const focusRef = useRef<HTMLDivElement>(null);
  const focusableElements = useRef<HTMLElement[]>([]);
  const currentFocusIndex = useRef(0);

  const updateFocusableElements = useCallback(() => {
    if (focusRef.current) {
      const elements = focusRef.current.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
      );
      focusableElements.current = Array.from(elements) as HTMLElement[];
    }
  }, []);

  const focusNext = useCallback(() => {
    updateFocusableElements();
    if (focusableElements.current.length > 0) {
      currentFocusIndex.current = (currentFocusIndex.current + 1) % focusableElements.current.length;
      focusableElements.current[currentFocusIndex.current]?.focus();
    }
  }, [updateFocusableElements]);

  const focusPrevious = useCallback(() => {
    updateFocusableElements();
    if (focusableElements.current.length > 0) {
      currentFocusIndex.current = currentFocusIndex.current === 0 
        ? focusableElements.current.length - 1 
        : currentFocusIndex.current - 1;
      focusableElements.current[currentFocusIndex.current]?.focus();
    }
  }, [updateFocusableElements]);

  const focusFirst = useCallback(() => {
    updateFocusableElements();
    if (focusableElements.current.length > 0) {
      currentFocusIndex.current = 0;
      focusableElements.current[0]?.focus();
    }
  }, [updateFocusableElements]);

  const focusLast = useCallback(() => {
    updateFocusableElements();
    if (focusableElements.current.length > 0) {
      currentFocusIndex.current = focusableElements.current.length - 1;
      focusableElements.current[currentFocusIndex.current]?.focus();
    }
  }, [updateFocusableElements]);

  return {
    focusRef,
    focusNext,
    focusPrevious,
    focusFirst,
    focusLast,
    updateFocusableElements
  };
};

// Hook for arrow key navigation in lists
export const useArrowKeyNavigation = <T>(
  items: T[],
  onSelect: (item: T, index: number) => void,
  options: {
    initialIndex?: number;
    loop?: boolean;
    onFocusChange?: (index: number) => void;
  } = {}
) => {
  const { initialIndex = 0, loop = true, onFocusChange } = options;
  const [focusedIndex, setFocusedIndex] = React.useState(initialIndex);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault();
        const prevIndex = focusedIndex > 0 ? focusedIndex - 1 : (loop ? items.length - 1 : 0);
        setFocusedIndex(prevIndex);
        onFocusChange?.(prevIndex);
        break;
      case 'ArrowDown':
        event.preventDefault();
        const nextIndex = focusedIndex < items.length - 1 ? focusedIndex + 1 : (loop ? 0 : items.length - 1);
        setFocusedIndex(nextIndex);
        onFocusChange?.(nextIndex);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        onSelect(items[focusedIndex], focusedIndex);
        break;
      case 'Home':
        event.preventDefault();
        setFocusedIndex(0);
        onFocusChange?.(0);
        break;
      case 'End':
        event.preventDefault();
        setFocusedIndex(items.length - 1);
        onFocusChange?.(items.length - 1);
        break;
    }
  }, [focusedIndex, items, onSelect, onFocusChange, loop]);

  return {
    focusedIndex,
    setFocusedIndex,
    handleKeyDown
  };
};

// Hook for modal keyboard navigation
export const useModalKeyboardNavigation = (onClose: () => void) => {
  const { handleKeyDown } = useKeyboardNavigation({
    onEscape: onClose,
    onTab: (event) => {
      // Handle tab trapping within modal
      const focusableElements = document.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;
      
      if (event?.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          firstElement?.focus();
        }
      }
    }
  });

  return { handleKeyDown };
};

export default useKeyboardNavigation;
